import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';

import { ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { AppState, BinaryFiles, ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import _ from 'lodash';

import { WhiteboardContext } from './WhiteboardContext';
import { checkPermission } from '../../../utils/permission';
import { usePrevious } from '../../../utils/usePrevious';
import { UserRoles } from '../../roster/types';
import { useCallbackRefState } from '../hooks/useCallbackRefState';
import { SocketState, useWebsocket } from '../hooks/useWebsocket';
import {
    AnyAction,
    ElementActions,
    PageActions,
    WhiteboardSessionActions,
    whiteboardSessionReducer,
    WhiteboardSessionState
} from '../state/WhiteboardSessionState';
import {
    Collaborator,
    Page,
    PageTypes,
    Session,
    SessionOptions,
    SessionTypes,
    UserInfo,
    InitializationStatus
} from '../types';
import { log } from '../util/misc';
import { reconcileElements } from '../util/reconciliation';
import {
    FIRST_PAGE_NOT_FOUND,
    PAGES_NOT_FOUND,
    SESSION_NOT_FOUND,
    USER_PAGE_CREATE_FAILED
} from '../whiteboard-errors';
import WhiteboardError from '../WhiteboardError';

export interface WhiteboardSessionProviderProps {
    sessionId: string;
}

interface WhiteboardSessionContextInterface {
    sessionId: string;
    sessionOptions: SessionOptions;
    socketState: SocketState;
    pushMessage: (data: any) => void;
    whiteboardSessionState: WhiteboardSessionState;
    dispatchWhiteboardSessionAction: React.Dispatch<AnyAction>;
    getOrCreateUserPage: (user: UserInfo, session: Session, otherPages: Page[]) => Promise<Page | undefined>;
    persist: (appState?: AppState) => string[];
    mergeRemoteChanges: (allLocalElements: ExcalidrawElement[], appState: AppState) => ExcalidrawElement[];
    changePage: (page: Page) => void;
    excalidrawAPIRef: { current: any };
    excalidrawAPI: ExcalidrawImperativeAPI | null;
    excalidrawRef: { current: any };
    ref: { current: any };
    excalidrawRefCallback: (excalidrawAPI: ExcalidrawImperativeAPI | null) => void;
    toolProperties: any;
    setToolProperties: (toolProperties: any) => void;
    isInControl: boolean;
    setIsInControl: (isInControl: boolean) => void;
    canvasLoading: boolean;
    setCanvasLoading: (canvasLoading: boolean) => void;
    showSettingsDialog: boolean;
    setShowSettingsDialog: (showSettingsDialog: boolean) => void;
}

const WhiteboardSessionContext = createContext(undefined as unknown as WhiteboardSessionContextInterface);

const { Provider, Consumer } = WhiteboardSessionContext;

/**
 * Manages state for a whiteboard session.
 * @param {WhiteboardSessionProviderProps} props
 */
const WhiteboardSessionProvider: React.FC<WhiteboardSessionProviderProps> = (props) => {
    const { children, sessionId } = props;
    const { deleteElements, userInfo, createPages, getPages, getSession, updatePresenter, updatePresenterPosition } =
        useContext(WhiteboardContext);

    const [whiteboardSessionState, dispatchWhiteboardSessionAction] = useReducer(whiteboardSessionReducer, {
        session: null,
        deleted: false,
        pages: [],
        presenter: null,
        presenterPosition: null,
        notFound: false,
        createdElements: [],
        updatedElements: [],
        deletedElementIds: [],
        remoteCreatedElements: [],
        remoteUpdatedElements: [],
        remoteDeletedElementIds: [],
        localFiles: {} as BinaryFiles
    });

    const [toolProperties, setToolProperties] = useState<any>({});
    const [isInControl, setIsInControl] = useState(false);
    const [canvasLoading, setCanvasLoading] = useState(true);
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [initializationStatus, setInitializationStatus] = useState<InitializationStatus>(
        InitializationStatus.NotStarted
    );

    const {
        presenter,
        presenterPosition,
        session,
        pages,
        currentPage,
        templatePage,
        createdElements,
        updatedElements,
        deletedElementIds,
        localFiles,
        remoteCreatedElements,
        remoteUpdatedElements,
        remoteDeletedElementIds
    } = whiteboardSessionState;

    const prevPresenter = usePrevious(presenter);

    const { socketState, pushMessage } = useWebsocket(session?.session_id, userInfo, dispatchWhiteboardSessionAction);

    const excalidrawAPIRef = useRef<ExcalidrawImperativeAPI>();
    const [excalidrawAPI, excalidrawRefCallback] = useCallbackRefState<ExcalidrawImperativeAPI>();
    excalidrawAPIRef.current = excalidrawAPI ?? undefined;

    /**
     * Get users' private page. For Individual whiteboard sessions, each user is taken to their own page. Students
     * are locked to their own page.
     *
     * Page will be associated to user by `type` set to `individual` and `created_by
     */
    const getOrCreateUserPage = useCallback(async () => {
        if (!userInfo || !session) return;

        const existingPrivatePage = pages.find(
            (p) => p.type === PageTypes.Individual && p.created_by === userInfo.student_id
        );

        if (existingPrivatePage) {
            return existingPrivatePage;
        }

        const [myNewPage] = await createPages([
            {
                session_id: session.session_id,
                name: userInfo.userName,
                order: pages.length,
                is_locked: false,
                write_access: UserRoles.Student,
                type: PageTypes.Individual
            }
        ]);

        if (!myNewPage) {
            throw new WhiteboardError(`${USER_PAGE_CREATE_FAILED}: User page could not be created.`);
        }

        // User page was nearly created, add it to all pages.
        dispatchWhiteboardSessionAction({
            type: PageActions.SetPages,
            payload: { pages: [...pages, myNewPage], userInfo }
        });

        return myNewPage;
    }, [userInfo, session, pages, createPages, dispatchWhiteboardSessionAction]);

    /**
     * Update presenter pointer position in excalidraw scene
     */
    useEffect(() => {
        if (!presenter) return;
        if (!presenterPosition) return;
        if (presenter.student_id === userInfo?.student_id) return;

        const collaborators = new Map<string, Collaborator>();

        collaborators.set('1', {
            pointer: { ...presenterPosition },
            username: presenter.userName,
            color: { background: '#4CAF50', stroke: '#4CAF50' }
        });

        excalidrawAPIRef.current?.updateScene({
            collaborators
        });
    }, [presenter, presenterPosition, userInfo?.student_id]);

    /**
     * Remove a presenter cursor when stopping presentation
     */
    useEffect(() => {
        if (!presenterPosition) {
            const collaborators = new Map<string, Collaborator>();
            excalidrawAPIRef.current?.updateScene({ collaborators });
        }
    }, [presenterPosition]);

    /**
     * Broadcast presenter page changes
     */
    useEffect(() => {
        if (!session || !presenter || !prevPresenter) return;
        if (presenter.student_id !== userInfo?.student_id) return;

        if (presenter.currentPageId && prevPresenter.currentPageId !== presenter.currentPageId) {
            updatePresenter(socketState.socket?.sessionId, session, {
                ...presenter,
                timestamp: _.now()
            });
        }
    }, [presenter, prevPresenter, session, updatePresenter, userInfo, socketState.socket?.sessionId]);

    /**
     * Load whiteboard session.
     */
    useEffect(() => {
        (async () => {
            if (sessionId && userInfo) {
                return;
            }

            const fetchedSession = await getSession(sessionId);
            if (!fetchedSession) {
                throw new WhiteboardError(`${SESSION_NOT_FOUND}: Session not found`);
            }

            // Setting session will trigger pages to load.
            dispatchWhiteboardSessionAction({
                type: WhiteboardSessionActions.Set,
                payload: { session: fetchedSession }
            });
            if (fetchedSession.data?.presenter) {
                dispatchWhiteboardSessionAction({
                    type: WhiteboardSessionActions.Present,
                    payload: { presenter: fetchedSession.data?.presenter }
                });
            }
        })();
    }, [dispatchWhiteboardSessionAction, getSession, sessionId, userInfo]);

    /**
     * Populate initial pages state.
     */
    useEffect(() => {
        const populateInitialPages = async () => {
            if (socketState.connectionStatus !== 'connected') return;
            if (!session || !userInfo) return;

            const sessionPages = await getPages(session.session_id);
            const filteredPages = sessionPages.filter((i) => i.name !== 'metadata'); // remove legacy metadata page

            if (!filteredPages.length) {
                throw new WhiteboardError(`${PAGES_NOT_FOUND}: No pages found.`);
            }

            dispatchWhiteboardSessionAction({
                type: PageActions.SetPages,
                payload: { pages: filteredPages, userInfo }
            });
        };

        populateInitialPages();
    }, [socketState.connectionStatus, getPages, userInfo, session]);

    /**
     * Set initial loaded page for group whiteboards
     */
    useEffect(() => {
        if (!session || !pages.length) return;
        if (session.type !== SessionTypes.Shared && session.type !== SessionTypes.Presentation) return;

        // eslint-disable-next-line prefer-destructuring
        const firstPage = pages.sort(({ order: a }, { order: b }) => a - b)[0];

        if (!firstPage) {
            throw new WhiteboardError(`${FIRST_PAGE_NOT_FOUND}: First page could not be found.`);
        }

        dispatchWhiteboardSessionAction({ type: PageActions.SetCurrentPage, payload: { page: firstPage } });
    }, [session, pages]);

    /**
     * Set initial loaded page for individual whiteboards
     */
    useEffect(() => {
        const initializeIndividualBoards = async () => {
            if (!userInfo || !session || !pages.length) return;
            if (session.type !== SessionTypes.Individual) return;
            if (initializationStatus !== InitializationStatus.NotStarted) return;

            setInitializationStatus(InitializationStatus.InProgress);

            let firstPage: Page | undefined;
            const initialPages = pages;

            if (templatePage) {
                if (userInfo.role === UserRoles.Student) {
                    firstPage = await getOrCreateUserPage();

                    if (firstPage && !pages.includes(firstPage)) {
                        dispatchWhiteboardSessionAction({
                            type: PageActions.SetPages,
                            payload: { pages: [...initialPages, firstPage], userInfo }
                        });
                    }
                }

                if (userInfo.role === UserRoles.Teacher || userInfo.role === UserRoles.Assistant) {
                    firstPage = templatePage;
                }
            }

            if (!templatePage) {
                firstPage = await getOrCreateUserPage();

                if (firstPage && !pages.includes(firstPage)) {
                    dispatchWhiteboardSessionAction({
                        type: PageActions.SetPages,
                        payload: { pages: [...initialPages, firstPage], userInfo }
                    });
                }
            }

            if (!firstPage) {
                throw new WhiteboardError(`${FIRST_PAGE_NOT_FOUND}: First page could not be found.`);
            }

            dispatchWhiteboardSessionAction({ type: PageActions.SetCurrentPage, payload: { page: firstPage } });
            setInitializationStatus(InitializationStatus.Complete);
        };

        initializeIndividualBoards();
    }, [session, pages, templatePage, userInfo, initializationStatus, getOrCreateUserPage]);

    /**
     * Persist local changes to backend from state.
     */
    const persist = useCallback(
        (appState?: AppState) => {
            if (!currentPage || !userInfo) {
                return [];
            }
            const presenting = !!presenter && presenter.student_id === userInfo?.student_id;

            const allIds = [...[...createdElements, ...updatedElements].map((e) => e.id), ...deletedElementIds];

            if (appState) {
                const { scrollX, scrollY, offsetTop, offsetLeft, width, height, zoom: localZoom, pointer } = appState;

                // If presenting and position and/or zoom changes: send update.
                if (presenting) {
                    const presenterInfo = {
                        student_id: userInfo.student_id,
                        userName: userInfo.userName,
                        picUrl: userInfo.picUrl,
                        role: userInfo.role
                    };
                    updatePresenter(sessionId, session!, {
                        ...presenter,
                        ...presenterInfo,
                        scrollX,
                        scrollY,
                        offsetTop,
                        offsetLeft,
                        width,
                        height,
                        zoom: localZoom,
                        currentPageId: currentPage.page_id,
                        timestamp: _.now()
                    });
                    updatePresenterPosition(pushMessage, session!, {
                        ...presenter,
                        ...presenterInfo,
                        scrollX,
                        scrollY,
                        offsetTop,
                        offsetLeft,
                        width,
                        height,
                        zoom: localZoom,
                        currentPageId: currentPage.page_id,
                        pointer,
                        timestamp: _.now()
                    });
                }
            }

            if (!allIds.length) {
                return [];
            }

            if (createdElements.length) {
                log('Local element creations', createdElements);

                if (presenting) {
                    const realCreatedElements = createdElements.filter(
                        (e) =>
                            _.isUndefined(e.customData) ||
                            _.isUndefined(e.customData.page_id) ||
                            e.customData.page_id === currentPage!.page_id
                    );

                    realCreatedElements
                        .map((element) => {
                            if (element.type === 'image' && localFiles && localFiles[element.fileId ?? '']) {
                                // Image elements need `key` property that matches the key used when uploading file to s3.
                                const extension = localFiles[element.fileId as string].mimeType.split('/')[1];
                                return { ...element, key: `${element.fileId}.${extension}` };
                            }
                            return element;
                        })
                        .forEach((updatedElement) => {
                            pushMessage({
                                channels: [`whiteboard-session:${sessionId}`],
                                type: 'generic',
                                body: { func: 'update_element', data: { element: updatedElement } }
                            });
                        });
                }
            }

            if (updatedElements.length) {
                log('Local element updates', updatedElements);

                if (presenting) {
                    updatedElements
                        .map((element) => {
                            if (element.type === 'image' && localFiles && localFiles[element.fileId ?? '']) {
                                // Image elements need `key` property that matches the key used when uploading file to s3.
                                const extension = localFiles[element.fileId as string].mimeType.split('/')[1];
                                return { ...element, key: `${element.fileId}.${extension}` };
                            }
                            return element;
                        })
                        .forEach((updatedElement) => {
                            pushMessage({
                                channels: [`whiteboard-session:${sessionId}`],
                                type: 'generic',
                                body: { func: 'update_element', data: { element: updatedElement } }
                            });
                        });
                }
                /*saveElements(
                    currentPage,
                    updatedElements.map((element) => {
                        if (element.type === 'image' && localFiles && localFiles[element.fileId ?? '']) {
                            // Image elements need `key` property that matches the key used when uploading file to s3.
                            const extension = localFiles[element.fileId as string].mimeType.split('/')[1];
                            return { ...element, key: `${element.fileId}.${extension}` };
                        }
                        return element;
                    }),
                    true
                );*/
                //websocketSave
            }

            if (deletedElementIds.length) {
                log('Local element deletions', deletedElementIds);

                // Delete elements remotely. Local elements will be hidden (isDeleted = true) until a websocket event
                // propagates the deletion.
                deleteElements(deletedElementIds);
            }

            return allIds;
        },
        [
            createdElements,
            currentPage,
            deleteElements,
            pushMessage,
            deletedElementIds,
            localFiles,
            presenter,
            session,
            updatePresenter,
            updatePresenterPosition,
            updatedElements,
            userInfo,
            sessionId
        ]
    );

    /**
     * Merge remote changes with Excalidraw elements. Return a reconciled list of elements.
     */
    const mergeRemoteChanges = useCallback(
        (allLocalElements: ExcalidrawElement[], appState: AppState) => {
            // Get element changes made by other participants. They are queued and ready to be applied to Excalidraw's state.
            const allIds = [
                ...[...remoteCreatedElements, ...remoteUpdatedElements].map((e) => e.id),
                ...remoteDeletedElementIds
            ];

            if (!allIds.length) {
                // No remote changes are present. Maybe next time.
                return allLocalElements;
            }

            // Get non-deleted remote elements that were changed.
            const remoteElements = [...remoteCreatedElements, ...remoteUpdatedElements]
                // Discard changes on elements that were deleted locally.
                .filter((e) => !(deletedElementIds as string[]).includes(e.id));

            if (remoteCreatedElements.length > 0) {
                log('Applying remote creations', remoteCreatedElements);
            }

            if (remoteUpdatedElements.length > 0) {
                log('Applying remote updates', remoteUpdatedElements);
            }

            if (remoteDeletedElementIds.length > 0) {
                log('Applying remote deletes', remoteDeletedElementIds);
            }

            // Magically merge remote changes with elements in Excalidraw.
            let elements = reconcileElements(allLocalElements, remoteElements, appState);

            elements = elements.filter((e) => !(remoteDeletedElementIds as string[]).includes(e.id));

            if (session?.type === SessionTypes.Individual) {
                elements = elements.map((element) => ({
                    ...element,
                    locked: element.customData?.page_id !== currentPage?.page_id
                }));
            }

            // Remove applied remote changes from state.
            dispatchWhiteboardSessionAction({ type: ElementActions.RemoteClear, payload: { elementIds: allIds } });

            return elements;
        },
        [
            remoteCreatedElements,
            remoteUpdatedElements,
            remoteDeletedElementIds,
            session?.type,
            deletedElementIds,
            currentPage?.page_id
        ]
    );

    const changePage = useCallback(
        (page: Page) => {
            if (!currentPage) return;

            const presenting = !!presenter && presenter.student_id === userInfo?.student_id;

            dispatchWhiteboardSessionAction({ type: PageActions.SetCurrentPage, payload: { page, presenting } });
        },
        [currentPage, presenter, userInfo?.student_id]
    );

    const sessionOptions: SessionOptions = useMemo(() => {
        if (!session || !userInfo) {
            return {
                allowPageNavigation: false,
                canEditPages: false,
                canLockPages: false,
                canPresent: false,
                canStopPresenting: false,
                displayEditingToggle: false,
                isPresentationViewer: false,
                presentationAccess: false,
                showPageList: false,
                studentEditingEnabled: false
            };
        }

        const presentationAccess = checkPermission(session.presentation_access, userInfo.role);
        const isPresentationViewer = !!presenter && presenter.student_id !== userInfo.student_id;

        return {
            allowPageNavigation: !isPresentationViewer,
            canEditPages: checkPermission(session.page_write_access, userInfo.role),
            canLockPages: checkPermission(session.page_lock_access, userInfo.role),
            canPresent: presentationAccess && !presenter,
            canStopPresenting: presentationAccess && !!presenter,
            displayEditingToggle: checkPermission(UserRoles.Assistant, userInfo.role),
            isPresentationViewer,
            presentationAccess,
            showPageList:
                session.type === SessionTypes.Shared ||
                session.type === SessionTypes.Presentation ||
                checkPermission(UserRoles.Assistant, userInfo.role),
            studentEditingEnabled: !!currentPage && currentPage.write_access === UserRoles.Student
        };
    }, [presenter, userInfo, currentPage, session]);

    return (
        <Provider
            value={{
                sessionId,
                socketState,
                pushMessage,
                whiteboardSessionState,
                dispatchWhiteboardSessionAction,
                getOrCreateUserPage,
                persist,
                mergeRemoteChanges,
                changePage,
                excalidrawAPIRef,
                excalidrawAPI,
                excalidrawRef: { current: {} },
                ref: { current: {} },
                excalidrawRefCallback,
                toolProperties,
                setToolProperties,
                sessionOptions,
                isInControl,
                setIsInControl,
                canvasLoading,
                setCanvasLoading,
                showSettingsDialog,
                setShowSettingsDialog
            }}
        >
            {children}
        </Provider>
    );
};

export { WhiteboardSessionContext, WhiteboardSessionProvider, Consumer as WhiteboardSessionConsumer };
